/**
 * Pull-to-refresh — a minimal touch-driven implementation that works
 * inside the Capacitor WebView (no extra native plugin required).
 *
 * Usage:
 *   const { bind, refreshing, distance } = usePullToRefresh(async () => {
 *     await reload();
 *   });
 *   return (
 *     <main {...bind} className="overflow-auto">
 *       <PullIndicator distance={distance} refreshing={refreshing} />
 *       …
 *     </main>
 *   );
 *
 * Mechanics:
 *   - Only engages when the scroll container is already at the top
 *     (scrollTop === 0) — otherwise the gesture is interpreted as a
 *     normal scroll.
 *   - Threshold = 80px before firing onRefresh.
 *   - Tracks finger distance for the visual indicator.
 *   - Cancels on touchcancel and on multi-touch (e.g. accidental zoom).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const THRESHOLD = 80;
const MAX_PULL = 120;

export function usePullToRefresh(onRefresh) {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const engaged = useRef(false);
  const elRef = useRef(null);

  const onTouchStart = useCallback((e) => {
    if (e.touches.length > 1) { startY.current = null; engaged.current = false; return; }
    const el = elRef.current;
    if (!el) return;
    if (el.scrollTop > 0) { startY.current = null; return; }
    startY.current = e.touches[0].clientY;
    engaged.current = false;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (startY.current == null || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy <= 0) { setDistance(0); engaged.current = false; return; }
    engaged.current = true;
    const damped = Math.min(MAX_PULL, dy * 0.5);
    setDistance(damped);
  }, [refreshing]);

  const onTouchEnd = useCallback(async () => {
    if (!engaged.current) { setDistance(0); startY.current = null; return; }
    if (distance >= THRESHOLD && !refreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        // Brief hold so the user sees the indicator finish
        setTimeout(() => { setRefreshing(false); setDistance(0); }, 280);
      }
    } else {
      setDistance(0);
    }
    startY.current = null;
    engaged.current = false;
  }, [distance, refreshing, onRefresh]);

  const onTouchCancel = useCallback(() => {
    setDistance(0); startY.current = null; engaged.current = false;
  }, []);

  // Bind handlers + the ref the hook uses to read scrollTop. Spread on
  // the scrollable element.
  //
  // ⚠️  DO NOT REMOVE the `distance > 0` guards on `transform` and
  //     `willChange`. They look redundant — `translateY(0px)` is a no-op,
  //     right? — but on iOS WKWebView a *resting* CSS `transform` on the
  //     scroll wrapper still creates a containing block for any
  //     `position: fixed` descendants AND quietly breaks subsequent touch
  //     event routing to sibling elements (TabBar, top tab pills). When
  //     this guard is removed, taps work ONCE per surface and then the
  //     entire bottom nav + top tab bar appear frozen. We've shipped
  //     this regression twice already (2026-02 + 2026-06). Keep the
  //     guards. The corresponding playwright regression test lives at
  //     `tests/frontend/nav_double_tap.spec` — if it goes red, this
  //     comment got ignored.
  const bind = {
    ref: elRef,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    style: {
      transform: distance > 0 ? `translateY(${distance}px)` : undefined,
      transition: refreshing ? "none" : "transform 200ms ease-out",
      willChange: distance > 0 || refreshing ? "transform" : undefined,
    },
  };

  return { bind, refreshing, distance, threshold: THRESHOLD };
}

/**
 * Indicator UI to drop in at the top of a pull-to-refresh container.
 * Renders a small spinning arc that fades in as the user pulls and spins
 * while refreshing.
 */
export function PullIndicator({ distance, refreshing, threshold = THRESHOLD }) {
  const progress = refreshing ? 1 : Math.min(1, distance / threshold);
  if (progress <= 0 && !refreshing) return null;
  return (
    <div
      className="absolute left-0 right-0 -top-12 flex items-center justify-center pointer-events-none z-10"
      style={{ opacity: progress }}
      data-testid="pull-to-refresh-indicator"
    >
      <div
        className={`w-8 h-8 rounded-full border-2 border-foreground/30 border-t-foreground ${
          refreshing ? "animate-spin" : ""
        }`}
        style={{ transform: refreshing ? "" : `rotate(${progress * 360}deg)` }}
      />
    </div>
  );
}
